import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search,
  Eye,
  Loader2,
  LogOut,
  Edit3,
  Trash2,
  Share2,
  Box,
  Building2,
  BarChart3
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Placeholder image for tours without thumbnails
const PLACEHOLDER_IMAGE = 'data:image/svg+xml,' + encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
    <rect fill="#2a2520" width="400" height="300"/>
    <rect fill="#3a3530" x="150" y="100" width="100" height="100" rx="10"/>
    <text fill="#8a7a6a" font-family="Arial" font-size="14" x="200" y="240" text-anchor="middle">No Preview</text>
  </svg>
`);

export function TourSelectionScreen({ onSelectTour }) {
  const { token, user, logout, canEdit } = useAuth();
  const { tours, loading: toursLoading, fetchTours, selectTour, deleteTour } = useTour();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedTourId, setSelectedTourId] = useState(null);

  useEffect(() => {
    if (token) {
      fetchTours();
    }
  }, [token, fetchTours]);

  // Get user's assigned tours
  const userTours = user?.role === 'admin' 
    ? tours 
    : tours.filter(tour => {
        const isClientTour = user?.client_id && tour.client_id === user.client_id;
        const hasDirectAccess = user?.tours_access?.includes(tour.id);
        return isClientTour || hasDirectAccess;
      });

  // Filter tours based on search
  const filteredTours = userTours.filter(tour => {
    const query = searchQuery.toLowerCase();
    return (
      tour.name?.toLowerCase().includes(query) ||
      tour.location?.toLowerCase().includes(query) ||
      tour.industry?.toLowerCase().includes(query) ||
      tour.job_number?.toLowerCase().includes(query) ||
      tour.client_name?.toLowerCase().includes(query) ||
      tour.equipment_type?.toLowerCase().includes(query) ||
      tour.stock_number?.toLowerCase().includes(query)
    );
  });

  // Sort tours
  const sortedTours = [...filteredTours].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      case 'oldest':
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      case 'name':
        return (a.name || '').localeCompare(b.name || '');
      case 'views':
        return (b.view_count || 0) - (a.view_count || 0);
      default:
        return 0;
    }
  });

  // Calculate stats
  const stats = useMemo(() => {
    const totalScans = userTours.length;
    const totalViews = userTours.reduce((sum, tour) => sum + (tour.view_count || 0), 0);
    const uniqueFacilities = new Set(userTours.map(t => t.location || t.client_name).filter(Boolean)).size;
    return { totalScans, totalViews, uniqueFacilities };
  }, [userTours]);

  const handleSelectTour = async (tour) => {
    setSelectedTourId(tour.id);
    
    // Increment view count
    try {
      await fetch(`${API_URL}/api/tours/${tour.id}/view`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) {
      console.warn('Could not track view:', e);
    }
    
    selectTour(tour);
    setTimeout(() => {
      onSelectTour(tour);
    }, 300);
  };

  const handleDeleteTour = async (e, tour) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete "${tour.name}"? This action cannot be undone.`)) {
      await deleteTour(tour.id);
    }
  };

  const handleShareTour = async (e, tour) => {
    e.stopPropagation();
    
    // Create shareable link using share_token (same as tour viewer share)
    const shareUrl = `${window.location.origin}/share/${tour.share_token}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert(`Link copied! Share "${tour.name}" with this link.`);
    } catch (err) {
      // Fallback
      prompt('Copy this link to share:', shareUrl);
    }
  };

  // Generate thumbnail URL from Matterport model ID
  const getThumbnailUrl = (tour) => {
    if (tour.thumbnail_url) {
      return tour.thumbnail_url;
    }
    if (tour.matterport_model_id) {
      return `https://my.matterport.com/api/v1/player/models/${tour.matterport_model_id}/thumb?width=400&height=300`;
    }
    return PLACEHOLDER_IMAGE;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F0E1' }}>
      {/* Header */}
      <header className="h-16 border-b px-4 flex items-center justify-between" style={{ backgroundColor: '#1A1A1A', borderColor: '#333' }}>
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_7a888182-d9ad-4911-af25-412e3d52e004/artifacts/sstxximv_Zentara%20As-Ltd._-01.png" 
            alt="Zentara" 
            className="h-10 w-auto object-contain"
          />
          <div>
            <h1 className="text-lg font-semibold leading-tight zentara-header" style={{ color: '#F7F3EC' }}>
              Zentara
            </h1>
            <p className="text-[10px]" style={{ color: '#888' }}>
              Industrial & Commercial Asset Management
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <span className="text-sm hidden sm:inline" style={{ color: '#888' }}>
            Welcome, {user?.name}
          </span>
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(197, 162, 74, 0.2)' }}>
            <span className="text-sm font-medium" style={{ color: '#C5A24A' }}>
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="hover:text-red-500"
            style={{ color: '#888' }}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6 md:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Client Logo */}
          {user?.client_logo_url && (
            <div className="text-center mb-8">
              <img 
                src={user.client_logo_url}
                alt={user.client_name || 'Client Logo'}
                className="h-16 sm:h-20 mx-auto object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            {/* Total Scans */}
            <div 
              className="rounded-xl p-5 flex items-center gap-4"
              style={{ backgroundColor: '#FAF5E8', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: 'rgba(197, 162, 74, 0.15)' }}
              >
                <Box className="h-6 w-6" style={{ color: '#C5A24A' }} />
              </div>
              <div>
                <p className="text-3xl font-bold" style={{ color: '#1A1A1A' }}>
                  {stats.totalScans}
                </p>
                <p className="text-sm" style={{ color: '#666' }}>
                  Total Scans
                </p>
                <p className="text-xs" style={{ color: '#999' }}>
                  Available to view
                </p>
              </div>
            </div>

            {/* Total Views */}
            <div 
              className="rounded-xl p-5 flex items-center gap-4"
              style={{ backgroundColor: '#FAF5E8', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)' }}
              >
                <BarChart3 className="h-6 w-6" style={{ color: '#3B82F6' }} />
              </div>
              <div>
                <p className="text-3xl font-bold" style={{ color: '#1A1A1A' }}>
                  {stats.totalViews}
                </p>
                <p className="text-sm" style={{ color: '#666' }}>
                  Total Views
                </p>
                <p className="text-xs" style={{ color: '#999' }}>
                  Across all scans
                </p>
              </div>
            </div>

            {/* Total Facilities */}
            <div 
              className="rounded-xl p-5 flex items-center gap-4"
              style={{ backgroundColor: '#FAF5E8', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)' }}
              >
                <Building2 className="h-6 w-6" style={{ color: '#10B981' }} />
              </div>
              <div>
                <p className="text-3xl font-bold" style={{ color: '#1A1A1A' }}>
                  {stats.uniqueFacilities || 1}
                </p>
                <p className="text-sm" style={{ color: '#666' }}>
                  Total Facilities
                </p>
                <p className="text-xs" style={{ color: '#999' }}>
                  Unique locations
                </p>
              </div>
            </div>
          </div>

          {/* Your Scans Section */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#1A1A1A' }}>
              Your Scans
            </h2>
            
            {/* Search and Sort Bar */}
            <div 
              className="rounded-xl p-4 flex flex-col sm:flex-row gap-4 items-stretch sm:items-center"
              style={{ backgroundColor: '#FAF5E8', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
            >
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#888' }} />
                <Input
                  placeholder="Search by facility, job number, equipment, stock number, or date..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 border-0"
                  style={{ 
                    backgroundColor: '#fff', 
                    color: '#333',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                  data-testid="tour-search-input"
                />
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger 
                  className="w-full sm:w-[180px] border-0"
                  style={{ 
                    backgroundColor: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                  <SelectItem value="name">Name A-Z</SelectItem>
                  <SelectItem value="views">Most Viewed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tours Grid */}
          {toursLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#C5A24A' }} />
            </div>
          ) : sortedTours.length === 0 ? (
            <div 
              className="text-center py-12 rounded-xl"
              style={{ backgroundColor: '#FAF5E8' }}
            >
              <Box className="h-12 w-12 mx-auto mb-4 opacity-50" style={{ color: '#888' }} />
              <p className="text-lg" style={{ color: '#666' }}>
                {searchQuery ? 'No scans match your search' : 'No scans available'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedTours.map((tour) => (
                <div
                  key={tour.id}
                  className={`rounded-xl overflow-hidden transition-all duration-300 ${
                    selectedTourId === tour.id 
                      ? 'ring-2 ring-offset-2 scale-[1.02]' 
                      : 'hover:shadow-xl hover:-translate-y-1'
                  }`}
                  style={{ 
                    backgroundColor: '#FAF5E8',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    ringColor: '#C5A24A'
                  }}
                  data-testid={`tour-card-${tour.id}`}
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-[4/3] overflow-hidden bg-gray-200">
                    <img
                      src={getThumbnailUrl(tour)}
                      alt={tour.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = PLACEHOLDER_IMAGE;
                      }}
                    />
                    
                    {/* View count badge */}
                    {tour.view_count > 0 && (
                      <div 
                        className="absolute top-3 right-3 px-2 py-1 rounded-full flex items-center gap-1 text-xs font-medium"
                        style={{ backgroundColor: 'rgba(0,0,0,0.7)', color: '#fff' }}
                      >
                        <Eye className="h-3 w-3" />
                        {tour.view_count}
                      </div>
                    )}
                    
                    {/* Admin controls */}
                    {canEdit && (
                      <div className="absolute top-3 left-3 flex gap-1">
                        <Button
                          variant="secondary"
                          size="icon"
                          className="h-7 w-7 bg-white/90 hover:bg-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Edit functionality would go here
                          }}
                          title="Edit"
                        >
                          <Edit3 className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="secondary"
                          size="icon"
                          className="h-7 w-7 bg-white/90 hover:bg-red-100"
                          onClick={(e) => handleDeleteTour(e, tour)}
                          title="Delete"
                        >
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    {/* Title */}
                    <h3 
                      className="font-semibold text-base leading-tight mb-2 line-clamp-2"
                      style={{ color: '#1A1A1A' }}
                    >
                      {tour.name}
                    </h3>

                    {/* Job Number */}
                    {tour.job_number && (
                      <p className="text-sm mb-3" style={{ color: '#666' }}>
                        Job #{tour.job_number}
                      </p>
                    )}

                    {/* Buttons */}
                    <div className="flex gap-2">
                      <Button
                        className="flex-1 font-semibold"
                        style={{ 
                          backgroundColor: '#C5A24A',
                          color: '#1A1A1A'
                        }}
                        onClick={() => handleSelectTour(tour)}
                        data-testid={`view-scan-${tour.id}`}
                      >
                        View 3D Scan
                      </Button>
                      <Button
                        variant="outline"
                        className="px-3"
                        style={{ 
                          borderColor: '#C5A24A',
                          color: '#C5A24A'
                        }}
                        onClick={(e) => handleShareTour(e, tour)}
                        title="Share Scan"
                      >
                        <Share2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default TourSelectionScreen;
